import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useUpdateGroupPolicy } from '../api/group-policies';
import { DUNNING_MASTER_ONLY } from '../model/types';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';
import { PartnerSection } from '../../policies/ui/PartnerSection';
import {
  editableRoles,
  hasPartnerRefusal,
  linksOf,
  partnerRefusalOf,
  seedPartnerValues,
  type PartnerFormValues,
} from '../../policies/model/partners';

/**
 * Changing the agreement's terms — ONE master version, ZERO member writes.
 *
 * That is not a property of this dialog, it is a property of the API: `UpdateGroupPolicy` cannot
 * name a child (service summary §5.3), and every member resolves what it inherits through the
 * master at read (decision D7). The copy says so, because an operator changing billing terms for
 * eight hundred bikes deserves to know that nothing is being written to eight hundred policies.
 *
 * It is DATED. A new agreement version takes effect from a business date the operator chooses —
 * never "now" — because that is what makes the previous terms reproducible afterwards.
 */
export function GroupPolicyEditDialog({
  code,
  groupPolicy,
  partnerRoles,
  open,
  onClose,
}: {
  readonly code: string;
  readonly groupPolicy: InstanceApi.GroupPolicy;
  /** The GROUP version's roles, through the pin (SDD-011 P-8): what the agreement may carry. */
  readonly partnerRoles: readonly InstanceApi.PartnerRoleRule[] | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const update = useUpdateGroupPolicy();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [frequency, setFrequency] = useState(groupPolicy.billing?.frequency ?? 'MONTHLY');
  const [submitted, setSubmitted] = useState(false);
  const rules = useMemo(() => editableRoles(partnerRoles, false), [partnerRoles]);
  const [partners, setPartners] = useState<PartnerFormValues>(() =>
    seedPartnerValues(
      rules,
      (groupPolicy.partners ?? []).map((partner) => partner.link ?? {}),
    ),
  );
  const refusal = partnerRefusalOf(update.error, rules, partners);

  const dateMissing = effectiveFrom === '';

  const close = (): void => {
    setSubmitted(false);
    setEffectiveFrom('');
    update.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('groupPolicies.editDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('groupPolicies.editDialog.description')}
          </Typography>

          <ConflictNotice conflict={update.conflict} />

          <TextField
            type="date"
            label={t('groupPolicies.editDialog.effectiveFrom')}
            value={effectiveFrom}
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            error={submitted && dateMissing}
            helperText={
              submitted && dateMissing
                ? t('groupPolicies.editDialog.dateRequired')
                : t('groupPolicies.editDialog.effectiveFromHint')
            }
          />

          <TextField
            select
            label={t('groupPolicies.overview.frequency')}
            value={frequency}
            fullWidth
            onChange={(event) => {
              setFrequency(event.target.value);
            }}
          >
            {(['MONTHLY', 'QUARTERLY', 'ANNUAL'] as const).map((option) => (
              <MenuItem key={option} value={option}>
                {t(`groupPolicies.frequency.${option}`)}
              </MenuItem>
            ))}
          </TextField>

          <PartnerSection
            rules={rules}
            values={partners}
            member={false}
            refusal={refusal}
            onChange={(roleErn, ids) => {
              setPartners((current) => ({ ...current, [roleErn]: ids }));
            }}
          />

          {/* Dunning is not offered as a choice: it exists only at master level (SSOT I-20). */}
          <Alert severity="info">{t('groupPolicies.editDialog.zeroMemberWrites')}</Alert>

          {update.isSuccess && (
            <Alert severity="success">{t('groupPolicies.editDialog.recorded')}</Alert>
          )}
          {update.isError && update.conflict === null && !hasPartnerRefusal(refusal) && (
            <Alert severity="error">{update.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={update.isPending}
          onClick={() => {
            setSubmitted(true);
            if (dateMissing) return;
            update.mutate({
              code,
              effectiveFrom,
              allowedTarifs: groupPolicy.allowedTarifs ?? [],
              billing: {
                ...groupPolicy.billing,
                frequency,
                dunning: DUNNING_MASTER_ONLY,
              },
              data: groupPolicy.data ?? {},
              // The COMPLETE list from the effective date on (SDD-011 P-7).
              partners: linksOf(rules, partners),
            });
          }}
        >
          {t('groupPolicies.editDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
